import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Tables } from '../lib/database.types';

const QUERY_KEY = 'contact_messages';

export const useContactMessages = (options = {}) => {
  const { isRead = null, limit = 50, offset = 0 } = options;

  return useQuery({
    queryKey: [QUERY_KEY, { isRead, limit, offset }],
    queryFn: async () => {
      let query = supabase
        .from(Tables.CONTACT_MESSAGES)
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (isRead !== null) {
        query = query.eq('is_read', isRead);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
};

export const useSendContactMessage = () => {
  return useMutation({
    mutationFn: async (message) => {
      const { data, error } = await supabase
        .from(Tables.CONTACT_MESSAGES)
        .insert({
          name: message.name,
          email: message.email,
          phone: message.phone || null,
          subject: message.subject || null,
          message: message.message,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
  });
};

export const useMarkMessageAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { data, error } = await supabase
        .from(Tables.CONTACT_MESSAGES)
        .update({ is_read: true })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useMarkMessageAsReplied = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { data, error } = await supabase
        .from(Tables.CONTACT_MESSAGES)
        .update({ 
          is_replied: true,
          replied_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteContactMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from(Tables.CONTACT_MESSAGES)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export default useContactMessages;
